// ---------------------------------------------------------------------------
// Control Plane Extension Tables (Council 9 spec)
// ---------------------------------------------------------------------------
// These live in the control plane DB alongside tenants, config, billing.
// NOT in per-tenant DBs.

import { pgTable, uuid, varchar, text, integer, numeric, boolean, jsonb, timestamp, index } from "drizzle-orm/pg-core";

const tz = { withTimezone: true } as const;

/** Agency commission ledger — tracks commission accrual and payout for agency model */
export const agencyCommissionLedger = pgTable(
  "agency_commission_ledger",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    orgId: uuid("org_id").notNull(), // agency organization
    tenantId: uuid("tenant_id").notNull(), // sub-account tenant
    subscriptionId: uuid("subscription_id").notNull(),
    billingEventId: uuid("billing_event_id").notNull(),
    commissionType: varchar("commission_type", { length: 20 }).notNull(), // recurring | one_time | usage_markup
    grossAmountCents: integer("gross_amount_cents").notNull(),
    commissionRate: numeric("commission_rate", { precision: 5, scale: 4 }).notNull(),
    commissionCents: integer("commission_cents").notNull(),
    currency: varchar("currency", { length: 3 }).notNull().default("EUR"),
    status: varchar("status", { length: 20 }).notNull().default("accrued"), // accrued | payable | paid | reversed
    payablePeriod: varchar("payable_period", { length: 7 }).notNull(), // '2026-03'
    paidAt: timestamp("paid_at", tz),
    paidReference: varchar("paid_reference", { length: 500 }),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_commission_org_period").on(table.orgId, table.payablePeriod),
    index("idx_commission_tenant").on(table.tenantId),
    index("idx_commission_status").on(table.status, table.payablePeriod),
  ],
);

/** Platform announcements — system-wide notifications from platform admin */
export const platformAnnouncements = pgTable(
  "platform_announcements",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    title: varchar("title", { length: 500 }).notNull(),
    body: text("body").notNull(),
    category: varchar("category", { length: 20 }).notNull(), // feature | maintenance | security | billing | general
    severity: varchar("severity", { length: 10 }).notNull().default("info"), // info | warning | critical
    targetAudience: varchar("target_audience", { length: 20 }).notNull().default("all"), // all | admins | agencies | plan:growth
    targetFilter: jsonb("target_filter").$type<Record<string, unknown>>(),
    publishedAt: timestamp("published_at", tz),
    expiresAt: timestamp("expires_at", tz),
    createdBy: uuid("created_by").notNull(),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_announcement_published").on(table.publishedAt, table.expiresAt),
    index("idx_announcement_category").on(table.category),
  ],
);
